import type { OnDestroy, OnInit } from '@angular/core';
import { Component, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MediaObserver } from '@angular/flex-layout';
import type { IOrderData } from '@shared/jp/interfaces';
import { Logger } from '@shared/common/logger';
import Subscriber from '@shared/common/subscriber';
import { RoutingService } from 'src/app/services/routing.service';
import { OrderService } from '@studiobuki/web-core/lib/order';
import { FirebaseService } from 'src/app/services/firebase.service';
import { ELoaderType, LoaderService } from '@studiobuki/web-core/lib/loader';
import { ROUTE_PATH_PARAM_ORDER_ID } from '@studiobuki/web-core/lib/routing';
import { getError } from '@shared/common/utils';
import { scrollToSelector } from 'src/utils';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import type { IOrderDataCommon } from '@shared/common/interfaces';
import type { IGiftOrderPageResolverData } from './gift-order-page.resolver';
import type { IFormData } from './interfaces';

const log = new Logger('GiftOrderPageComponent');

@Component({
  templateUrl: './gift-order-page.component.html',
  styleUrls: ['./gift-order-page.component.scss'],
})
export class GiftOrderPageComponent implements OnInit, OnDestroy {
  /** used to touch forms to make errors appear */
  public readonly touchEventEmitter = new EventEmitter<void>();

  public readonly order$: BehaviorSubject<IOrderData>;

  private _form: IFormData = {};

  public step: 1 | 2 = 1;

  public steps = 1;

  public isNextDisabled = false;

  get isCompleted(): boolean {
    return this.step > this.steps;
  }

  private _sub = new Subscriber();

  constructor(
    private _route: ActivatedRoute,
    private _routing: RoutingService,
    private _orderService: OrderService,
    private _firebaseService: FirebaseService,
    private _loaderService: LoaderService,
    public media: MediaObserver,
  ) {
    const { data, paramMap } = this._route.snapshot;

    const { order } = data.resolved as IGiftOrderPageResolverData;

    if (!paramMap.get(ROUTE_PATH_PARAM_ORDER_ID)) {
      this._routing.goToGiftOrder(order.orderId).catch((e) => log.error(e));
    }

    this.order$ = new BehaviorSubject<IOrderData>(order);
  }

  async ngOnInit() {
    // const { order } = this;
    let order = await firstValueFrom(this.order$);
    const userId = await this._firebaseService.getUser().then((_) => _.uid);

    try {
      const handled = await this._handleOrder(order);

      if (!handled) {
        order = await this._firebaseService.waitForUserOrder(
          order.orderId,
          (_order) => {
            const hasBarOrderId =
              !!_order && 'barOrderId' in _order && !!_order.barOrderId;
            const hasError = !!_order && 'error' in _order && !!_order.error;

            return hasBarOrderId || hasError;
          },
        );

        await this._handleOrder(order);
      }
    } catch (e: any) {
      await this._handleError(e, userId, order.orderId);
    }
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  public onEditCartClick() {
    return this._goToBook();
  }

  public onBackClick() {
    return this._goToBook();
  }

  public async onNextClick() {
    const isValid = this._validate();

    log.info('onNextClick', { isValid });

    if (!isValid) return;

    this._loaderService.show({ type: ELoaderType.payment, data: {} });
    await this._updateOrderData();
  }

  public onFormChange(data: IFormData) {
    this._form = data;
    this.isNextDisabled = false;
  }

  private async _goToBook() {
    const order = this.order$.value;

    const book = order.books[0];

    if (!book) {
      alert(getError('book not found').message);
      return;
    }

    const giftId = order.gift;

    if (!giftId) {
      alert(getError('gift not found').message);
      return;
    }

    await this._routing.goToBook(book.bookId, giftId);
  }

  private _validate() {
    const { formEmail, formShippingAddress } = this._form;

    const isFormEmailValid = !!formEmail;

    const isFormShippingAddressValid = !!formShippingAddress;

    if (!isFormEmailValid) {
      scrollToSelector('lib-form-email');
    } else if (!isFormShippingAddressValid) {
      scrollToSelector('lib-form-stripe-address');
    }

    const isValid = isFormEmailValid && isFormShippingAddressValid;

    this.isNextDisabled = !isValid;
    this.touchEventEmitter.next();

    return isValid;
  }

  private async _updateOrderData() {
    const { formEmail, formShippingAddress } = this._form;
    const order = this.order$.value;

    if (!formEmail || !formShippingAddress)
      throw getError('missing required data');

    order.addressShippingData = {
      ...formShippingAddress,
      email: formEmail.email,
    };

    this.order$.next(order);

    return this._orderService.updateOrder(
      order.orderId,
      order as IOrderDataCommon,
    );
  }

  private async _handleError(
    e: any,
    userId: string,
    orderId: string | undefined,
  ) {
    /** showing the main error in console for developers */
    log.error(
      `userId: "${userId}"; orderId: "${orderId}";`,
      e.message || e.error || e,
    );

    /** showing user readable error */
    alert(
      // TODO: change to japanese
      'An error occurred while processing your order. Please try again.',
    );

    await this._routing.goToIndex();
    this._loaderService.hide();
  }

  private _handleComplete() {
    this._loaderService.hide();
    // prevent doubling
    if (this.step === 2) return;
    this.step = 2;
  }

  private async _handleOrder(order: IOrderData) {
    this.order$.next(order);

    const { error, barOrderId } = order;

    if (error) {
      throw new Error(error);
    }

    if (barOrderId) {
      this._handleComplete();

      return true;
    }

    return false;
  }
}
